<template>
  <v-container id="quote" fluid>
    <v-row>
      <v-col cols="12" v-if="notifications.length > 0">
        <v-alert v-for="(notification, key) in notifications" type="warning" :key="key" closable>{{
          notification
        }}</v-alert>
      </v-col>

      <v-col cols="12" v-if="worktopArchiveWarnings.any" class="py-1">
        <v-alert type="warning" color="orange" density="compact" icon="mdi-alert-outline" class="mb-0">
          <strong>Worktop attention needed:</strong> {{ worktopWarningText }}. Open the <strong>Worktops</strong> tab to
          review.
        </v-alert>
      </v-col>

      <v-col cols="12" md="8">
        <!-- MATERIALS -->
        <v-card class="compact mb-4 elevation-1">
          <v-card-title class="bg-secondary text-subtitle-1">
            <div>Materials</div>
            <v-spacer></v-spacer>
            <v-icon @click="addMaterial">mdi-plus</v-icon>
          </v-card-title>

          <v-card-text>
            <v-row v-for="(material, key) in version.materials" :key="key" class="panel-row panel-row--unlabelled py-2">
              <v-col cols="3" class="d-flex align-center">
                <v-menu :disabled="!material.displayColour" :close-on-content-click="false">
                  <template v-slot:activator="{ props }">
                    <div class="colour" :style="{ 'background-color': material.displayColour }" v-bind="props"></div>
                  </template>
                  <v-color-picker v-model="material.displayColour" mode="hexa"></v-color-picker>
                </v-menu>
                <v-autocomplete
                  v-model="material.value"
                  :items="materialItems(material)"
                  item-title="text"
                  placeholder="Material"
                  :disabled="materialLockStatus[material.id]"
                  @update:model-value="onMaterialChange(key, material.value)"
                  hide-details
                ></v-autocomplete>
              </v-col>

              <v-col cols="2">
                <v-autocomplete
                  v-if="material.value"
                  v-model="material.thickness"
                  :items="getMaterialThicknesses(material)"
                  :disabled="materialLockStatus[material.id]"
                  placeholder="Thickness"
                  hide-details
                ></v-autocomplete>
              </v-col>
              <v-col cols="2">
                <v-autocomplete
                  v-if="material.value"
                  v-model="material.size"
                  :items="getSheetSizes(material, material.thickness)"
                  @update:model-value="onMaterialSizeChange(key, material.size)"
                  placeholder="Sheet Size"
                  hide-details
                ></v-autocomplete>
              </v-col>

              <v-col cols="2" v-if="material.value && !Materials.hasGrain(material)">
                <v-autocomplete
                  v-model="material.sides"
                  :items="getSheetSides(material, material.thickness, material.size)"
                  :disabled="materialLockStatus[material.id]"
                  placeholder="Sheet Sides"
                  hide-details
                ></v-autocomplete>
              </v-col>

              <v-col
                :cols="Materials.hasGrain(material) ? 4 : 2"
                class="d-flex align-center material-options"
                v-if="material.value"
              >
                <!-- Laminates -->
                <v-autocomplete
                  v-if="!Materials.hasGrain(material)"
                  v-model="material.colourCode"
                  :items="getColourCodes(material)"
                  :item-title="
                    (i) => {
                      return typeof i === 'string' ? i : i.value + ' - ' + i.text
                    }
                  "
                  @update:model-value="onColourChange(key)"
                  placeholder="Colour"
                  hide-details
                ></v-autocomplete>

                <!-- Wood Faced -->
                <v-checkbox
                  v-if="Materials.hasGrain(material)"
                  v-model="material.unfinished"
                  :disabled="materialLockStatus[material.id]"
                  label="Unfinished"
                  hide-details
                  class="mr-4"
                ></v-checkbox>
                <v-checkbox
                  v-if="Materials.hasGrain(material)"
                  v-model="material.continuousGrain"
                  :disabled="materialLockStatus[material.id]"
                  label="Continuous"
                  hide-details
                ></v-checkbox>
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="1" class="d-flex">
                <v-spacer></v-spacer>
                <v-icon @click="removeMaterial(key)" style="flex: 0 1 auto !important">mdi-delete</v-icon>
              </v-col>
              <v-col cols="12" class="py-0" v-if="supersedingMaterialName(material)">
                <v-chip size="small" label color="info" class="font-weight-medium text-white">
                  <v-icon size="small" start>mdi-information-outline</v-icon>
                  There is a newer version of {{ supersedingMaterialName(material) }}; if you'd like to update, please
                  select the new material.
                </v-chip>
              </v-col>
              <v-col cols="12" class="py-0" v-else-if="discontinuedMaterialName(material)">
                <v-chip size="small" label color="warning" class="font-weight-medium text-white">
                  <v-icon size="small" start>mdi-alert-outline</v-icon>
                  {{ discontinuedMaterialName(material) }} is no longer available; the archived version will be used
                  unless you select a different material.
                </v-chip>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- HANDLES -->
        <v-card class="compact mb-4 elevation-1">
          <v-card-title class="bg-secondary text-subtitle-1">
            <div>Handles</div>
            <v-spacer></v-spacer>
            <v-icon @click="addHandle">mdi-plus</v-icon>
          </v-card-title>
          <v-card-text>
            <v-row
              v-for="(handle, key) in version.handles"
              :key="handle.id"
              class="panel-row panel-row--unlabelled py-2"
            >
              <v-col cols="4">
                <v-autocomplete
                  v-model="handle.value"
                  :items="handles"
                  :disabled="handleLockStatus[handle.id]"
                  placeholder="Select handle"
                  @update:model-value="constructHandleLabel(handle)"
                  hide-details
                ></v-autocomplete>
              </v-col>
              <v-col cols="4" v-if="requiresInsert(handle)">
                <v-select
                  v-model="handle.insert"
                  :items="
                    version.materials.filter((material) => {
                      return material.thickness == 4 && (material.hasGrain || material.sides === 1)
                    })
                  "
                  item-title="label"
                  item-value="id"
                  :disabled="handleLockStatus[handle.id]"
                  placeholder="Insert material"
                  no-data-text="No 4mm materials created"
                  @update:model-value="constructHandleLabel(handle)"
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="3" v-if="getOrientations(handle)">
                <v-select
                  v-model="handle.orientation"
                  :items="getOrientations(handle)"
                  :disabled="handleLockStatus[handle.id]"
                  placeholder="Position"
                  @update:model-value="constructHandleLabel(handle)"
                  hide-details
                ></v-select>
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="1" class="d-flex justify-end">
                <v-icon @click="duplicateHandle(key)">mdi-content-copy</v-icon>
                <v-icon @click="removeHandle(key)" style="flex: 0 1 auto !important">mdi-delete</v-icon>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Settings -->
        <v-card class="mb-4 elevation-1">
          <v-card-title class="bg-secondary text-subtitle-1">
            <div>Settings</div>
          </v-card-title>
          <v-card-text class="pt-6">
            <v-row>
              <v-col cols="12" md="7">
                <v-row id="grain-direction">
                  <v-col cols="12" class="d-flex flex-row align-start">
                    <v-select
                      v-model="version.grainDirection"
                      label="Grain Direction"
                      :disabled="disableGrainSelection"
                      :items="productData.grains"
                      :item-title="
                        (i) => {
                          return Object.values(i)[0]
                        }
                      "
                      :item-value="
                        (i) => {
                          return i
                        }
                      "
                      @update:model-value="resetItemOpposingGrain"
                      class="mt-0"
                    ></v-select>
                    <v-card variant="outlined" class="mx-3 default-grain">
                      <v-card-subtitle class="font-weight-bold mx-2"
                        >Default Grain = {{ Object.values(this.defaultGrain)[0] }}</v-card-subtitle
                      >
                      <v-spacer></v-spacer>
                      <div
                        v-if="itemListOpposingGrain.length > 0"
                        class="mx-2 mb-2 d-flex flex-column justify-center align-center"
                      >
                        <p class="text-decoration-underline my-0">Item with opposing grain:</p>
                        <v-chip-group v-for="(item, key) of itemListOpposingGrain" :key="key">
                          <v-chip style="cursor: default">{{
                            item.name
                              ? 'Urtil: ' + item.name + ' - [H' + item.h + ' x W' + item.w + ']'
                              : 'BK: ' + item.itemNum + ' - [H' + item.h + ' x W' + item.w + ']'
                          }}</v-chip>
                        </v-chip-group>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
                <v-row id="early-items" v-if="quote.region !== 'eu'">
                  <v-col cols="6" v-if="version.spacerPanels.length > 0">
                    <v-switch v-model="version.earlySpacers" label="Spacers Early" class="mt-0"></v-switch>
                  </v-col>
                  <v-col cols="6" v-if="version.worktops.length > 0">
                    <v-switch v-model="version.earlyWorktops" label="Worktops Early"></v-switch>
                  </v-col>
                </v-row>
                <v-switch
                  v-if="quote.region === 'uk'"
                  v-model="version.excludeVat"
                  label="Exclude VAT"
                  :disabled="!$store.state.user.permissions.admin"
                ></v-switch>
                <v-select
                  v-model="quote.region"
                  label="Region"
                  :items="Object.values(productData.regions)"
                  @update:model-value="
                    () => {
                      this.materials = Materials.getMaterialsByRegion(this.quote.region)
                      this.version.deliveryLocation = null
                      this.updateDeliveryLocations()
                    }
                  "
                ></v-select>
                <v-card border class="pa-3 mt-2 mb-4">
                  <v-autocomplete
                    v-model="version.deliveryLocation"
                    label="Delivery Location"
                    placeholder="Delivery Location"
                    hide-details
                    :items="deliveryLocations"
                    :item-title="
                      (i) => {
                        return typeof i === 'string' ? '' : i.data().name
                      }
                    "
                    :item-value="
                      (i) => {
                        return typeof i === 'string' ? i : i.id
                      }
                    "
                  ></v-autocomplete>
                  <v-switch
                    :model-value="version.deliveryOverride === 'free'"
                    @update:model-value="(v) => (version.deliveryOverride = v ? 'free' : null)"
                    label="Free Delivery"
                    class="mt-3"
                    hide-details
                  ></v-switch>
                  <v-switch
                    :model-value="version.deliveryOverride === 'collection'"
                    @update:model-value="(v) => (version.deliveryOverride = v ? 'collection' : null)"
                    label="Customer Collection"
                    class="mt-0"
                    hide-details
                  ></v-switch>
                  <div class="text-caption text-medium-emphasis mt-3">
                    Toggling Free Delivery or Customer Collection will remove incorporated delivery fees within line
                    item costs.
                  </div>
                </v-card>
                <v-select v-model="version.frontTypes" :items="frontTypes" label="Front Types" multiple> </v-select>
                <v-text-field v-model.number="version.discount" label="Fronts Discount" suffix="%"></v-text-field>
                <v-text-field
                  v-model.number="version.discountWorktops"
                  label="Worktop Discount"
                  suffix="%"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="5">
                <v-textarea
                  auto-grow
                  class="mt-1"
                  v-model="version.notes"
                  placeholder="Place any notes here..."
                  variant="outlined"
                  label="Notes"
                ></v-textarea>
                <v-textarea
                  v-if="version.changeStatement"
                  readonly
                  auto-grow
                  class="mt-2"
                  v-model="version.changeStatement"
                  variant="outlined"
                  label="Changes"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Additional Items -->
      <v-col cols="12" md="4">
        <v-card class="mb-4">
          <v-card-title class="bg-secondary text-subtitle-1">
            <div>Additional Items</div>
            <v-spacer></v-spacer>
            <v-icon @click="addAdditionalItem(false)">mdi-plus</v-icon>
          </v-card-title>
          <v-card-text class="pt-3">
            <v-row v-for="(item, key) in additionalItemsPacked" :key="key" class="panel-row">
              <v-col cols="5">
                <v-text-field
                  label="Item"
                  v-model="item.description"
                  :rules="[rules.required]"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="3">
                <v-text-field
                  label="Price Ex-Vat"
                  v-model.number="item.value"
                  type="number"
                  step="0.01"
                  :rules="[rules.required, rules.number, rules.maxTwoDecimals]"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="3">
                <v-text-field
                  label="Discount"
                  v-model.number="item.discount"
                  type="number"
                  :rules="[rules.required, rules.number]"
                  hide-details="auto"
                  suffix="%"
                ></v-text-field>
              </v-col>
              <v-col cols="1" class="d-flex justify-end">
                <v-icon @click="removeAdditionalItems(item)" style="flex: 0 1 auto !important">mdi-delete</v-icon>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
          <v-card-title class="bg-secondary text-subtitle-1">
            <div>Additional Costs</div>
            <v-spacer></v-spacer>
            <v-icon @click="addAdditionalItem(true)">mdi-plus</v-icon>
          </v-card-title>
          <v-card-text class="pt-3">
            <v-row v-for="(item, key) in additionalItemsExcludeFromPacked" :key="key" class="panel-row">
              <v-col cols="5">
                <v-text-field
                  label="Item"
                  v-model="item.description"
                  :rules="[rules.required]"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="3">
                <v-text-field
                  label="Price Ex-Vat"
                  v-model.number="item.value"
                  type="number"
                  step="0.01"
                  :rules="[rules.required, rules.number, rules.maxTwoDecimals]"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="3">
                <v-text-field
                  label="Discount"
                  v-model.number="item.discount"
                  type="number"
                  :rules="[rules.required, rules.number]"
                  hide-details="auto"
                  suffix="%"
                ></v-text-field>
              </v-col>
              <v-col cols="1" class="d-flex justify-end">
                <v-icon @click="removeAdditionalItems(item)" style="flex: 0 1 auto !important">mdi-delete</v-icon>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mb-4" id="the-numbers" v-if="this.fronts">
          <v-card-title class="bg-secondary text-subtitle-1">
            <div>The Numbers</div>
          </v-card-title>
          <v-card-text class="pt-6">
            <section class="mb-3" v-if="version.fronts.length > 0 || version.coverPanels.length > 0 || true">
              <div class="d-flex mb-3">
                <div class="">Fronts, cover panels & spacers</div>
              </div>

              <v-divider class="mb-4"></v-divider>
              <v-container class="pa-0">
                <v-row v-for="material in numberedMaterials" class="d-flex" :key="material.id">
                  <v-col cols="8" class="d-flex align-center">
                    <div class="colour mr-2" :style="{ 'background-color': material.displayColour }"></div>
                    <div class="text-truncate">
                      {{ material.thickness + 'mm - ' + material.label }}
                    </div>
                  </v-col>
                  <v-col
                    cols="4"
                    class="d-flex align-center justify-end"
                    :class="fronts[material.id].total === 0 ? 'text-red' : ''"
                    v-if="quote.region == 'uk'"
                  >
                    {{ $numeral(fronts[material.id].total * (1 - version.discount / 100)).format('$0,0.00') }}
                    <span
                      class="ml-1"
                      v-if="fronts[material.id].pricing"
                      :class="
                        fronts[material.id].pricing.cogs >= $store.state.pricing.cog.ukCog.value
                          ? 'text-red'
                          : 'text-green'
                      "
                      >{{ '/ ' + fronts[material.id].pricing.cogs + '%' }}</span
                    >
                  </v-col>
                  <v-col
                    cols="4"
                    class="d-flex align-center justify-end"
                    :class="fronts[material.id].total === 0 ? 'text-red' : ''"
                    v-else
                  >
                    {{ $numeral(fronts[material.id].total * (1 - version.discount / 100)).format('$0,0.00') }}
                    <small class="ml-1">{{
                      fronts[material.id].handlesCost > 0 && fronts[material.id].total > 0
                        ? `(${$numeral(fronts[material.id].handlesCost).format('$0,0.00')} handles)`
                        : ''
                    }}</small>
                  </v-col>
                </v-row>
              </v-container>
            </section>

            <div class="d-flex justify-space-between" v-if="spacerPanels.totalLength > 0">
              <div>Spacer Panels</div>
              <div>
                {{ $numeral(spacerPanels.totalPrice * (1 - version.discount / 100)).format('$0,0.00') }}
              </div>
            </div>

            <div class="d-flex justify-space-between mb-1" v-if="version.worktops.length > 0">
              <div>Worktops</div>
              <div>
                {{ $numeral(worktops.total).format('$0,0.00') }}
              </div>
            </div>

            <div class="d-flex justify-space-between mb-1" v-if="version.urtils && version.urtils.length > 0">
              <div>{{ version.urtils.length === 1 ? 'Ürtil' : 'Ürtils' }}</div>
              <div>{{ $numeral(urtils.total).format('$0,0.00') }}</div>
            </div>

            <div class="d-flex justify-space-between mb-1" v-if="version.pantries && version.pantries.length > 0">
              <div>{{ version.pantries.length === 1 ? 'Pantry' : 'Pantries' }}</div>
              <div>{{ $numeral(pantries.total).format('$0,0.00') }}</div>
            </div>

            <div class="d-flex justify-space-between mb-1" v-if="version.spiceRacks && version.spiceRacks.length > 0">
              <div>{{ version.spiceRacks.length === 1 ? 'Spice Rack' : 'Spice Racks' }}</div>
              <div>{{ $numeral(spiceRacks?.total || 0).format('$0,0.00') }}</div>
            </div>

            <div class="d-flex justify-space-between mb-1" v-for="item in valuedAdditionalItems" :key="item.id">
              <div>{{ item.description }}</div>
              <div>
                {{ $numeral(parseFloat(item.value) * (1 - item.discount / 100)).format('$0,0.00') }}
              </div>
            </div>

            <v-divider class="my-3" />

            <div v-if="quote.region == 'uk'">
              <div class="d-flex justify-space-between">
                <div>Delivery</div>
                <div>
                  {{ shipping === 0 ? 'Included' : $numeral(shipping).format('$0,0.00') }}
                </div>
              </div>
              <div class="d-flex justify-space-between">
                <div>Subtotal</div>
                <div>
                  {{ $numeral(discountedSubtotal + shipping).format('$0,0.00') }}
                </div>
              </div>
              <div class="d-flex justify-space-between">
                <div>VAT</div>
                <div>{{ $numeral(vatAmount).format('$0,0.00') }}</div>
              </div>
              <v-divider class="my-2" />
              <div class="d-flex justify-space-between text-high-emphasis">
                <div><b>Total</b></div>
                <div>
                  <b>{{ $numeral(discountedSubtotal + vatAmount + shipping).format('$0,0.00') }}</b>
                </div>
              </div>
            </div>
            <div v-else class="totals">
              <div class="d-flex justify-space-between">
                <div>Delivery</div>
                <div>{{ $numeral(shipping).format('$0,0.00') }}</div>
              </div>
              <div class="d-flex justify-space-between">
                <div>Total</div>
                <div>
                  {{ $numeral(discountedSubtotal + shipping).format('$0,0.00') }}
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Material Usage -->
        <v-card id="material-usage" v-if="materialUsage" class="mb-4">
          <v-card-title class="bg-secondary text-subtitle-1">
            <div>Material Usage</div>
          </v-card-title>
          <v-card-text class="">
            <div v-for="material in usedMaterials" class="d-flex mt-4" :key="material.id">
              <div class="colour mr-2" :style="{ 'background-color': material.displayColour }"></div>
              <div>
                {{ material.thickness + 'mm - ' + material.label }}
              </div>
              <v-spacer />
              <div>
                {{ (materialUsage[material.id].areaWithWastage / (material.w * material.h)).toFixed(3) }}
                sheets
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Material Delete Confirmation -->
    <v-dialog v-model="materialDeleteVisible" persistent width="400">
      <v-card>
        <v-card-title>Delete Material</v-card-title>
        <v-card-text>
          Are you sure you want to delete "{{ materialDeleteParams.label }}"? <br /><small
            >This material is not being used anywhere.</small
          >
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="materialDeleteVisible = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="confirmDeleteMaterial">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Material Reassign -->
    <v-dialog v-model="materialReassignVisible" persistent width="500">
      <v-card>
        <v-card-title> Reassign Material </v-card-title>
        <v-card-text>
          <div v-if="materialReassignParams.usedIn" class="mb-3">
            This material is used in: <strong>{{ materialReassignParams.usedIn.join(', ') }}</strong>
          </div>
          <v-radio-group v-model="materialReassignParams.replace" @update:model-value="checkReassignMaterial()">
            <v-radio
              v-for="material in materialReassignOptions"
              :key="material.id"
              :label="
                material.thickness +
                'mm ' +
                material.label +
                (material.hasGrain ? ' (veneer)' : material.sides === 1 ? ' (single)' : ' (double)')
              "
              :value="material.id"
            ></v-radio>
          </v-radio-group>
        </v-card-text>
        <v-alert v-if="reassignMaterialWarning !== ''" type="warning">
          {{ reassignMaterialWarning }}
        </v-alert>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="text"
            @click="
              () => {
                materialReassignVisible = false
                reassignMaterialWarning = ''
              }
            "
            >Cancel</v-btn
          >
          <v-btn
            color="accent"
            variant="flat"
            @click="
              () => {
                reassignMaterial()
                reassignMaterialWarning = ''
              }
            "
            >Confirm</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Handle Delete Confirmation -->
    <v-dialog v-model="handleDeleteVisible" persistent width="400">
      <v-card>
        <v-card-title>Delete Handle</v-card-title>
        <v-card-text>
          Are you sure you want to delete "{{ handleDeleteParams.label }}"? <br /><small
            >This handle is not being used anywhere.</small
          >
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="handleDeleteVisible = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="confirmDeleteHandle">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Handle Reassign -->
    <v-dialog v-model="handleReassignVisible" persistent width="500">
      <v-card>
        <v-card-title> Reassign Handle </v-card-title>
        <v-card-text>
          <div class="mb-3">This handle is being used on fronts and must be reassigned before deletion.</div>
          <v-radio-group v-model="handleReassignParams.replace">
            <v-radio
              v-for="handle in handleReassignOptions"
              :key="handle.id"
              :label="handle.label"
              :value="handle.id"
            ></v-radio>
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="handleReassignVisible = false">Cancel</v-btn>
          <v-btn color="accent" variant="flat" @click="reassignHandle">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import uid from 'uid'
import productData from '@/js/productData'
import Materials from '@/js/Materials'
import * as MaterialResolver from '@/js/MaterialResolver'
import * as WorktopResolver from '@/js/WorktopResolver'
import usPricing from '@/js/pricing/usPricing'
import ukPricing from '@/js/pricing/ukPricing'
import mu from '@/js/mu'
import { db } from '@/js/db'
import { collection, getDocs, query, where } from 'firebase/firestore'

export default {
  name: 'quote',
  props: ['config', 'quote', 'version'],
  data() {
    return {
      productData: productData,
      debug: false,
      Materials: Materials,
      rules: {
        required: (v) => (v !== null && v !== undefined && v !== '') || 'Required',
        number: (v) => (v !== null && v !== undefined && v !== '' && !isNaN(Number(v))) || 'Must be a number',
        maxTwoDecimals: (v) =>
          v === null || v === undefined || v === '' || /^-?\d+(\.\d{1,2})?$/.test(String(v)) || 'Max 2 decimal places'
      },
      handles: this.$store.state.handles || [],
      materialReassignVisible: false,
      materialReassignParams: {},
      reassignMaterialWarning: '',
      materialDeleteVisible: false,
      materialDeleteParams: {},
      handleReassignVisible: false,
      handleReassignParams: {},
      handleDeleteVisible: false,
      handleDeleteParams: {},
      notifications: [],
      materialUsage: null,
      fronts: null,
      urtils: null,
      pantries: null,
      spiceRacks: null,
      shipping: 0,
      mu: null,
      deliveryLocations: [],
      frontTypes: [
        { text: 'Metod', value: 'metod' },
        { text: 'Pax / Platsa', value: 'paxPlatsa' },
        { text: 'No Fronts', value: 'none' }
      ],
      itemListOpposingGrain: [],
      disableGrainSelection: false
    }
  },
  async created() {
    await MaterialResolver.prefetchMaterialsForVersion(this.version)
    await WorktopResolver.prefetchWorktopsForVersion(this.version)

    for (let material of this.version.materials) {
      if (material.value) {
        if (material.materialId) continue

        const resolved = MaterialResolver.resolveMaterial(material)

        if (!resolved) {
          delete material.value
          delete material.size
          delete material.w
          delete material.h
          delete material.thickness
          continue
        }

        this.reconcileMaterialFromLive(material, resolved)
      }
    }

    // update legacy worktop thicknesses to int from string
    for (const worktop of this.version.worktops) {
      if (typeof worktop.thickness === 'string') {
        worktop.thickness = parseInt(worktop.thickness)
      }
    }

    // Set GBP as default if none present
    if (!this.version.currency) this.version.currency = 'GBP'
    this.$numeral.locale(this.version.currency)

    // set discountWorktops if doesn't exist
    if (!this.version.discountWorktops) {
      this.version.discountWorktops = 0
    }

    // set default region if doesn't exist
    if (!this.quote.region) {
      this.quote.region = 'uk'
    }

    // Deal with material prop changes
    await this.updateDeliveryLocations()
    this.runCalculations()
    this.checkIncompleteItems()
    this.legacyDefaultGrain()
    this.getItemListOpposingGrain()
    this.legacyUPlight()
    this.checkForPreAssembledUrtil()
  },
  computed: {
    masterMaterials() {
      return Materials.getMaterialsByRegion(this.quote.region) || []
    },
    additionalItemsPacked() {
      if (!this.version.additionalItems) return null
      return this.version.additionalItems.filter((o) => {
        return !o.excludeFromPacked
      })
    },
    numberedMaterials() {
      return (this.version.materials || []).filter((material) => this.displayMaterialNumbers(material))
    },
    valuedAdditionalItems() {
      return (this.version.additionalItems || []).filter((item) => item.value)
    },
    usedMaterials() {
      return (this.version.materials || []).filter(
        (material) => material.value && Object.keys(this.materialUsage).includes(material.id)
      )
    },
    materialReassignOptions() {
      return (this.version.materials || []).filter(
        (material) =>
          material.id != this.materialReassignParams.removeId &&
          material.thickness == this.materialReassignParams.thickness &&
          (material.hasGrain ||
            this.materialReassignParams.hasGrain ||
            material.sides === this.materialReassignParams.sides ||
            this.materialReassignParams.frontsOnly)
      )
    },
    handleReassignOptions() {
      return (this.version.handles || []).filter((handle) => handle.id != this.handleReassignParams.removeId)
    },
    additionalItemsExcludeFromPacked() {
      if (!this.version.additionalItems) return null
      return this.version.additionalItems.filter((o) => {
        return o.excludeFromPacked
      })
    },
    worktops() {
      var o = {
        cost: 0,
        total: 0,
        incomplete: false,
        debug: {
          numWorktops: this.version.worktops.length,
          items: []
        }
      }

      if (this.quote.region == 'us') {
        usPricing.worktops(o, this.version, this.notifications)
        return o
      } else {
        return ukPricing.worktops(
          this.version.worktops,
          this.version.exchangeRates ? this.version.exchangeRates.rates[this.version.currency] : 1
        )
      }
    },
    worktopArchiveWarnings() {
      const region = this.quote.region
      const worktops = this.version.worktops || []
      let discontinued = 0
      let superseded = 0
      let colourGone = 0
      for (const w of worktops) {
        if (WorktopResolver.isWorktopDiscontinued(w, region)) discontinued++
        else if (WorktopResolver.getSupersedingLiveWorktop(w, region)) superseded++
        if (WorktopResolver.isColourDiscontinued(w, region)) colourGone++
      }
      return { discontinued, superseded, colourGone, any: discontinued + superseded + colourGone > 0 }
    },
    worktopWarningText() {
      const w = this.worktopArchiveWarnings
      const parts = []
      if (w.discontinued) parts.push(`${w.discontinued} discontinued worktop${w.discontinued > 1 ? 's' : ''}`)
      if (w.superseded) parts.push(`${w.superseded} worktop${w.superseded > 1 ? 's' : ''} with a newer version`)
      if (w.colourGone) parts.push(`${w.colourGone} discontinued colour${w.colourGone > 1 ? 's' : ''}`)
      return parts.join(', ')
    },
    spacerPanels() {
      var o = {
        totalLength: 0,
        totalPrice: 0,
        incomplete: false
      }

      for (var i = 0; i < this.version.spacerPanels.length; i++) {
        if (
          this.version.spacerPanels[i].l &&
          this.version.spacerPanels[i].qty &&
          this.version.spacerPanels[i].material
        ) {
          o.totalLength += this.version.spacerPanels[i].l * this.version.spacerPanels[i].qty
        } else {
          o.incomplete = true
        }
      }

      let spacerPanelsMultiplier =
        this.quote.region == 'uk'
          ? this.$store.state.pricing.ukMultiplier.externalMarkup.value
          : this.$store.state.pricing.usMultiplier.lucidMarkup.value

      o.totalPrice = this.$utils.toFixedNumber(
        this.convertedValue(
          (o.totalLength / 1000) * productData.spacerCostPerLinearMetre[this.quote.region] * spacerPanelsMultiplier
        ),
        2
      )
      return o
    },
    additionalItems() {
      var o = { full: 0, discounted: 0 }

      if (this.version.additionalItems) {
        for (const item of this.version.additionalItems) {
          if (!item.description || !item.value) continue

          const discount = item.discount ? item.discount : 0

          o.full += this.$utils.toFixedNumber(parseFloat(item.value), 2)
          o.discounted += this.$utils.toFixedNumber(parseFloat(item.value * (1 - discount / 100)), 2)
        }
      }

      return o
    },
    subtotal() {
      const sub =
        this.fronts.total +
        this.spacerPanels.totalPrice +
        this.worktops.total +
        this.urtils.total +
        this.pantries.total +
        (this.spiceRacks?.total || 0) +
        this.additionalItems.full
      return this.$utils.toFixedNumber(sub, 2)
    },
    discountedSubtotal() {
      const r = (n) => Math.round(n * 100) / 100
      const frontsDiscount = 1 - this.version.discount / 100
      const worktopsDiscount = 1 - this.version.discountWorktops / 100

      let total = 0

      // Fronts — one invoice line per material
      this.version.materials.forEach((material) => {
        if (this.fronts[material.id] && this.fronts[material.id].total) {
          total += r(this.fronts[material.id].total * frontsDiscount)
        }
      })

      // Spacer panels (share the fronts discount)
      total += r(this.spacerPanels.totalPrice * frontsDiscount)

      // Worktops
      total += r(this.worktops.total * worktopsDiscount)

      // Ürtils / Pantries / Spice racks — no discount, totals already rounded
      total += this.urtils.total + this.pantries.total + (this.spiceRacks?.total || 0)

      // Additional items — already rounded per item in additionalItems.discounted
      total += this.additionalItems.discounted

      return r(total)
    },
    vatAmount() {
      if (this.quote.region !== 'uk' || this.version.excludeVat) return 0

      const r = (n) => Math.round(n * 100) / 100
      const frontsDiscount = 1 - this.version.discount / 100
      const worktopsDiscount = 1 - this.version.discountWorktops / 100

      let vat = 0

      // Fronts — one line per material
      this.version.materials.forEach((material) => {
        if (this.fronts[material.id] && this.fronts[material.id].total) {
          vat += r(r(this.fronts[material.id].total * frontsDiscount) * 0.2)
        }
      })

      // Spacer Panels (share the fronts discount)
      vat += r(r(this.spacerPanels.totalPrice * frontsDiscount) * 0.2)

      // Worktops
      vat += r(r(this.worktops.total * worktopsDiscount) * 0.2)

      // Ürtils
      if (this?.urtils?.total) {
        vat += r(r(this.urtils.total) * 0.2)
      }

      // Pantries
      if (this?.pantries?.total) {
        vat += r(r(this.pantries.total) * 0.2)
      }

      // Spice Racks
      if (this?.spiceRacks?.total) {
        vat += r(r(this.spiceRacks.total) * 0.2)
      }

      // Additional Items — one line each, discounted per item
      if (this.version.additionalItems) {
        for (const item of this.version.additionalItems) {
          if (!item.description || !item.value) continue

          const itemDiscount = parseFloat(item?.discount) > 0 ? (100 - parseFloat(item?.discount)) / 100 : 1
          vat += r(r(parseFloat(item.value) * itemDiscount) * 0.2)
        }
      }

      // Shipping
      vat += r(r(this.shipping) * 0.2)

      return vat
    },
    defaultGrain() {
      return this.findGrainDirection()
    },
    materialLockStatus() {
      if (!this.fronts || !this.urtils || !this.pantries || !this.spiceRacks) return {}
      const status = {}
      for (const material of this.version.materials) {
        // Only lock if material is BOTH in use AND complete
        status[material.id] = this.isMaterialInUse(material.id).inUse && this.isMaterialComplete(material)
      }
      return status
    },
    handleLockStatus() {
      const status = {}
      for (const handle of this.version.handles) {
        status[handle.id] = this.isHandleInUse(handle.id)
      }
      return status
    },
    unusedMaterials() {
      if (!this.fronts || !this.urtils || !this.pantries || !this.spiceRacks) return []
      return this.version.materials.filter((m) => {
        // Skip empty materials (only have id)
        if (Object.keys(m).length === 1) return true
        return !this.isMaterialInUse(m.id).inUse
      })
    },
    incompleteItems() {
      const issues = []

      // Check materials in settings
      for (const material of this.version.materials) {
        // Skip empty materials (only have id)
        if (Object.keys(material).length === 1) continue

        const reason = this.materialIncompleteReason(material)
        if (reason) {
          const label = material.label || material.value || 'Unnamed'
          issues.push(`Incomplete material in settings: ${label} (${reason})`)
        }
      }

      // Check fronts
      for (const front of this.version.fronts) {
        if (!front.w || !front.h || !front.qty || !front.material || !front.itemNum || !front.handle) {
          issues.push(`Front item ${front.itemNum || '(no item#)'}: missing required fields`)
        }
      }

      // Check cover panels
      for (const cp of this.version.coverPanels) {
        if (!cp.w || !cp.h || !cp.qty || !cp.material || !cp.coverPanelType) {
          issues.push(`Cover panel: missing required fields`)
        }
        // V-plinth specific checks
        if (cp.coverPanelType === 'v-plinth' && (cp.vent == null || cp.ventRef == null || cp.ventOffset == null)) {
          issues.push(`V-plinth: missing vent configuration`)
        }
        // Lighting specific checks
        if (cp.lighting && !['r', 'l', 'c'].includes(cp.lightingRef)) {
          issues.push(`Cover panel ${cp.itemNum || ''}: lighting set but missing position (right, left or center)`)
        }
      }

      // Check spacer panels
      for (const sp of this.version.spacerPanels) {
        if (!sp.l || !sp.qty || !sp.material) {
          issues.push(`Spacer panel: missing required fields`)
        }
      }

      // Check worktops
      for (const wt of this.version.worktops) {
        if (!wt.material || !wt.thickness || !wt.faces) {
          issues.push(`Worktop: missing required fields`)
        }
      }

      // Check urtils
      if (this.version.urtils) {
        for (const urtil of this.version.urtils) {
          if (!urtil.w || !urtil.h || !urtil.d || !urtil.carcass || !urtil.back || !urtil.type) {
            issues.push(`Urtil ${urtil.name || '(unnamed)'}: missing required fields`)
          }
          // Check door materials if not open type
          if (urtil.type !== 'o') {
            if (!urtil.material1) {
              issues.push(`Urtil ${urtil.name || '(unnamed)'}: missing door material`)
            }
            if (urtil.type === 't' && !urtil.material2) {
              issues.push(`Urtil ${urtil.name || '(unnamed)'}: missing second door material`)
            }
          }
          // Check door coverage
          if (urtil.type === 's' && !urtil.doorCoverageOne) {
            issues.push(`Urtil ${urtil.name || '(unnamed)'}: missing door coverage`)
          }
          if (urtil.type === 't' && (!urtil.doorCoverageOne || !urtil.doorCoverageTwo)) {
            issues.push(`Urtil ${urtil.name || '(unnamed)'}: missing door coverage`)
          }
          // Check shelving qty
          if (urtil.shelving && urtil.shelvingOptions?.qty < 1) {
            issues.push(`Urtil ${urtil.name || '(unnamed)'}: shelving enabled but no shelves configured`)
          }
        }
      }

      // Check pantries
      if (this.version.pantries) {
        for (const pantry of this.version.pantries) {
          if (!pantry.w || !pantry.h || !pantry.d || !pantry.carcass || !pantry.back || !pantry.pantryType) {
            issues.push(`Pantry: missing required fields`)
          }
          if (!pantry.pantryHinge) {
            issues.push(`Pantry ${pantry.name || '(unnamed)'}: missing hinge selection`)
          }
          if (!pantry.name) {
            issues.push(`Pantry: missing name`)
          }
          if (pantry.pantryType === 'l' && !pantry.base) {
            issues.push(`Pantry ${pantry.name || '(unnamed)'}: missing laminate base material`)
          }
          if (pantry.pantryType === 'l' && pantry.base === pantry.carcass) {
            issues.push(`Pantry ${pantry.name || '(unnamed)'}: laminate base cannot match carcass material`)
          }
        }
      }

      return issues
    },
    canOpenVisual() {
      return (
        this.unusedMaterials.length === 0 &&
        this.incompleteItems.length === 0 &&
        this.invalidAdditionalItems.length === 0
      )
    },
    invalidAdditionalItems() {
      const isBlank = (v) => v === null || v === undefined || v === ''
      const isNotNumber = (v) => isBlank(v) || isNaN(Number(v))
      const tooManyDecimals = (v) => !isBlank(v) && !/^-?\d+(\.\d{1,2})?$/.test(String(v))
      const issues = []
      for (const item of this.version.additionalItems || []) {
        const label = item.description || 'Unnamed additional item'
        if (isBlank(item.description)) issues.push('An additional item is missing a description')
        if (isNotNumber(item.value)) issues.push(`"${label}" needs a numeric price`)
        else if (tooManyDecimals(item.value)) issues.push(`"${label}" price can have at most 2 decimal places`)
        if (isNotNumber(item.discount)) issues.push(`"${label}" needs a numeric discount`)
      }
      return issues
    },
    visualBlockedReason() {
      const reasons = []
      if (this.unusedMaterials.length > 0) {
        const labels = this.unusedMaterials.map((m) => m.label || 'Unnamed').join(', ')
        reasons.push(`Unused materials: ${labels}`)
      }
      if (this.incompleteItems.length > 0) {
        reasons.push(...this.incompleteItems.slice(0, 3))
        if (this.incompleteItems.length > 3) {
          reasons.push(`...and ${this.incompleteItems.length - 3} more issues`)
        }
      }
      if (this.invalidAdditionalItems.length > 0) {
        reasons.push(...this.invalidAdditionalItems.slice(0, 3))
        if (this.invalidAdditionalItems.length > 3) {
          reasons.push(`...and ${this.invalidAdditionalItems.length - 3} more additional-item issues`)
        }
      }
      return reasons
    }
  },
  watch: {
    version: {
      handler() {
        this.runCalculations()
      },
      deep: true
    },
    '$store.state.pricing': {
      handler(newVal) {
        if (newVal) {
          this.runCalculations()
        }
      },
      deep: true
    },
    '$store.state.handles': {
      handler(newVal) {
        if (newVal) {
          this.handles = newVal
          this.runCalculations()
        }
      }
    }
  },
  methods: {
    checkAndOpenVisual() {
      // Called by parent component to validate and open visual
      const reasons = this.visualBlockedReason
      this.$emit('openVisual', {
        canOpen: this.canOpenVisual,
        reasons: reasons
      })
    },
    isMaterialInUse(materialId) {
      const usedIn = []

      // Check fronts & cover panels
      if (this.fronts[materialId]?.itemCount > 0) {
        usedIn.push('fronts/cover panels')
      }

      // Check urtils (carcass, back, doors)
      if (this.urtils[materialId]?.itemCount > 0) {
        usedIn.push('urtils')
      }

      // Check pantries
      if (this.pantries[materialId]?.itemCount > 0) {
        usedIn.push('pantries')
      }

      // Check spice racks
      if (this.spiceRacks && this.spiceRacks[materialId]?.itemCount > 0) {
        usedIn.push('spice racks')
      }

      // Check handles (insert material)
      const handleCount = this.version.handles.filter((h) => h.insert === materialId).length
      if (handleCount > 0) {
        usedIn.push('handles')
      }

      return { inUse: usedIn.length > 0, usedIn }
    },
    isMaterialUsedOnlyInFronts(materialId) {
      const inFronts = this.fronts && this.fronts[materialId]?.itemCount > 0
      const inUrtils = this.urtils && this.urtils[materialId]?.itemCount > 0
      const inPantries = this.pantries && this.pantries[materialId]?.itemCount > 0
      const inSpiceRacks = this.spiceRacks && this.spiceRacks[materialId]?.itemCount > 0
      const inHandles = this.version.handles?.some((h) => h.insert === materialId)

      return inFronts && !inUrtils && !inPantries && !inSpiceRacks && !inHandles
    },
    isMaterialComplete(material) {
      // Empty material (just has id) is always incomplete
      if (Object.keys(material).length <= 1) return false
      return this.materialIncompleteReason(material) === null
    },
    materialIncompleteReason(material) {
      if (!material.value) return 'no material selected'

      const resolved = MaterialResolver.resolveMaterial(material)
      if (!resolved) return 'material no longer available'

      if (!material.thickness) return 'sheet thickness not selected'
      if (!material.size) return 'sheet size not selected'

      // The chosen thickness + size must still be offered by the current doc.
      const sizeOffered = (resolved.sizes || []).some(
        (s) => s.thickness == material.thickness && `${s.w}x${s.h}` === material.size
      )
      if (!sizeOffered) return 'selected sheet size no longer available — re-pick'

      // Laminates require a side count; wood-faced (grain) materials don't.
      if (!resolved.hasGrain && !material.sides) return 'sheet sides not selected'

      if (!resolved.hasGrain && resolved.colours !== undefined && !material.colourCode) return 'colour not selected'

      return null
    },
    isHandleInUse(handleId) {
      return this.version.fronts.some((front) => front.handle === handleId)
    },
    displayMaterialNumbers(material) {
      // checks
      if (
        !material.thickness ||
        !material.value ||
        !material.id ||
        !this.fronts[material.id] ||
        !this.fronts[material.id]?.total > 0
      ) {
        return false
      }

      if (this.quote.region == 'uk') {
        return true
      } else {
        // US region, only show if material is NOT 4mm
        if (material.thickness && material.thickness !== 4) {
          return true
        }
      }
    },
    findGrainDirection() {
      const veneerFronts = this.version.fronts?.filter((front) => this.getMaterial(front.material)?.hasGrain)
      const veneerUrtils = this.version.urtils?.filter((urtil) => this.getMaterial(urtil.back)?.hasGrain)

      //check if job requires grain direction (only laminate front/urtil/pantry)
      const veneerBackPanels = this.version.coverPanels?.filter((coverPanel) => {
        if (coverPanel.coverPanelType === 'back') {
          if (this.getMaterial(coverPanel.material)?.hasGrain) {
            return true
          } else {
            return false
          }
        } else {
          return false
        }
      })
      if (!(veneerFronts?.length > 0 || veneerUrtils?.length > 0 || veneerBackPanels?.length > 0)) {
        this.version.grainDirection = { none: 'N/A Grain Direction' }
        this.disableGrainSelection = true
        return { none: 'N/A Grain Direction' }
      }

      // check if there are any fronts with width > 1210 (very unlikely)
      veneerFronts.forEach((front) => {
        if (front.w > 1210) {
          this.version.grainDirection = { hoz: 'Horizontal Grain' }
          this.disableGrainSelection = true
          return { hoz: 'Horizontal Grain' }
        }
      })

      // stack same item fronts & collect all heights in array for algorithm check
      let checkHeights = []
      let frontsStackedHeights = []
      if (veneerFronts.length > 0) {
        // create groups of stacks by ITEMS
        let groupByItemNum = {}
        veneerFronts.forEach((front) => {
          const itemNum = front['itemNum']
          if (itemNum in groupByItemNum) {
            groupByItemNum[itemNum].push(front)
          } else {
            groupByItemNum[itemNum] = [front]
          }
        })
        // find stack height by QTY
        const stackByQty = (front) => {
          let stackHeight = 0
          // console.log(front)
          let specialType = front.frontType && front.frontType.includes('-') ? front.frontType.split('-')[2] : null
          let qty = ['2part', 'cnr'].includes(specialType) ? 1 : front.qty
          for (let i = 0; i < qty; i++) {
            stackHeight += front.h
          }
          return stackHeight
        }
        // then stack up QTY of ITEMS
        Object.values(groupByItemNum).forEach((group) => {
          let totalItemHeight = 0
          let previousFront = null
          group.forEach((front) => {
            if (!previousFront) {
              previousFront = front
              totalItemHeight += stackByQty(front)
            }
            // same itemNum fronts but not lhd+rhd, so stacked
            else if (
              !(
                (previousFront.frontType.split('-')[0] == 'lhd' && front.frontType.split('-')[0] == 'rhd') ||
                (previousFront.frontType.split('-')[0] == 'rhd' && front.frontType.split('-')[0] == 'lhd')
              )
            ) {
              // multiple qty, so stacked
              totalItemHeight += stackByQty(front)
              previousFront = front
            }
          })
          frontsStackedHeights.push(totalItemHeight)
        })
      }
      checkHeights.push(frontsStackedHeights)
      // collect all urtil heights in array
      let urtilsHeights = []
      if (veneerUrtils.length > 0) {
        veneerUrtils.forEach((urtil) => {
          urtilsHeights.push(urtil.h)
        })
        checkHeights.push(urtilsHeights)
      }
      let tallestHeight
      if (checkHeights.length == 1) {
        tallestHeight = checkHeights.map((subArray) => {
          return subArray.reduce((acc, curr) => {
            return curr > acc ? curr : acc
          }, 0)
        })
      } else {
        tallestHeight = checkHeights
          .map((subArray) => {
            return subArray.reduce((acc, curr) => {
              return curr > acc ? curr : acc
            }, 0)
          })
          .reduce((acc, curr) => {
            return curr > acc ? curr : acc
          }, 0)
      }
      let defaultGrain
      if (tallestHeight > 1200) {
        this.version.grainDirection = { vert: 'Vertical Grain' }
        this.disableGrainSelection = true
        defaultGrain = { vert: 'Vertical Grain' }
      } else {
        defaultGrain = { hoz: 'Horizontal Grain' }
      }

      return defaultGrain
    },
    runCalculations() {
      // Guard against running calculations before store data is ready
      if (!this.$store.state.handles || !this.$store.state.pricing) {
        return
      }

      let exchangeRate = this.version.exchangeRates ? this.version.exchangeRates.rates[this.version.currency] : 1
      if (this.quote.region == 'us') {
        this.fronts = usPricing.fronts(
          this.version.fronts.concat(this.version.coverPanels),
          this.version.materials,
          this.version.handles
        )

        // WIP - split US and UK urtils pricing
        this.urtils = usPricing.urtils(this.version.urtils, this.version.materials, exchangeRate)
      } else {
        this.fronts = ukPricing.fronts(
          this.version.fronts.concat(this.version.coverPanels),
          this.version.materials,
          this.version.handles,
          exchangeRate
        )

        this.urtils = ukPricing.urtils(this.version.urtils, this.version.materials, exchangeRate)
      }

      this.pantries = ukPricing.pantries(this.version.pantries, this.version.materials, exchangeRate)
      this.spiceRacks = ukPricing.spiceRacks(this.version.spiceRacks, this.version.materials)
      this.notifications = this.notifications.concat(this.fronts.notifications)
      this.mu = mu.get(this.version)
      this.updateMaterialUsage()
      this.updateShipping()
      if (this.quote.region == 'uk') this.updateCog()

      this.$emit(
        'draftQuoteUpdated',
        {
          dateCreated: this.$utils.tsNow(),
          fronts: this.fronts,
          worktops: this.worktops,
          urtils: this.urtils,
          pantries: this.pantries,
          spiceRacks: this.spiceRacks,
          spacerPanels: this.spacerPanels,
          shipping: this.shipping,
          subtotal: this.subtotal,
          discountedSubtotal: this.discountedSubtotal,
          vatAmount: this.vatAmount,
          total: this.$utils.toFixedNumber(this?.discountedSubtotal + this?.vatAmount + this?.shipping, 2)
        },
        this.materialUsage,
        this.mu
      )
    },
    checkIncompleteItems() {
      if (this.fronts.incomplete) {
        this.notifications.push(
          'There are incomplete fronts or cover panels in the quote which are being ignored. Complete or remove these items before publishing.'
        )
      }
      if (this.spacerPanels.incomplete) {
        this.notifications.push(
          'There are incomplete spacer panels in the quote which are being ignored. Complete or remove these items before publishing.'
        )
      }
      if (this.worktops.incomplete) {
        this.notifications.push(
          'There are incomplete worktops in the quote which are being ignored. Complete or remove these items before publishing.'
        )
      }
      if (this.urtils.incomplete) {
        this.notifications.push(
          'There are incomplete Ürtils in the quote which are being ignored. Complete or remove these items before publishing.'
        )
      }
      if (this.pantries.incomplete) {
        this.notifications.push(
          'There are incomplete Pantries in the quote which are being ignored. Complete or remove these items before publishing.'
        )
      }
      if (this.spiceRacks?.incomplete) {
        this.notifications.push(
          'There are incomplete Spice Racks in the quote which are being ignored. Complete or remove these items before publishing.'
        )
      }
    },
    updateShipping() {
      if (this.version.deliveryLocation) {
        if (this.version.deliveryOverride) {
          this.shipping = 0
          return
        }

        // get the shipping zone for delivery location
        const shippingZone = this.deliveryLocations.find((l) => {
          return l.id === this.version.deliveryLocation
        })

        // calculate the shipping cost for shipping zone
        let shippingCost
        if (this.quote.region === 'eu') {
          const baseCost = shippingZone?.data().cost
          const customsClearance = shippingZone?.data().customsClearance || 0
          const insurancePct = shippingZone?.data().insurance || 0
          shippingCost = this.convertedValue(baseCost + customsClearance + (baseCost * insurancePct) / 100)
        } else {
          shippingCost = this.convertedValue(shippingZone?.data().cost)
        }
        shippingCost = this.$utils.toFixedNumber(shippingCost, 2)

        // For UK hide full cost of shipping. For EU and US incorporate half the shipping cost
        const incorporatedShippingCost =
          this.quote.region === 'uk'
            ? this.$utils.toFixedNumber(shippingCost, 2)
            : this.$utils.toFixedNumber(shippingCost / 2, 2)

        // calculate how much we add add delivery charges
        let deliveryCharge
        if (this.quote.region === 'uk') {
          deliveryCharge = this.fronts.total === 0 ? shippingCost : 0
        } else {
          deliveryCharge = shippingCost / 2
        }

        let shippingCostProportionedTotal = 0
        let lastAdjustedPricing

        // split shipping cost across materials

        for (const material of this.version.materials) {
          const materialPricing = this.fronts[material.id]

          if (materialPricing && materialPricing?.total > 0) {
            // keep a reference of last adjusted material incase we need to adjust for rounding
            lastAdjustedPricing = materialPricing

            const incorporatedShippingCostProportioned = this.$utils.toFixedNumber(
              incorporatedShippingCost * (materialPricing?.total / this.fronts?.total),
              2
            )

            // add shipping cost to material pricing for calcCogs
            materialPricing.incorporatedShippingCost = incorporatedShippingCostProportioned

            materialPricing.total = this.$utils.toFixedNumber(
              materialPricing?.total + incorporatedShippingCostProportioned,
              2
            )

            shippingCostProportionedTotal = this.$utils.toFixedNumber(
              shippingCostProportionedTotal + incorporatedShippingCostProportioned,
              2
            )
          }
        }

        // making any rounding adjustment
        const roundingAdjustment = this.$utils.toFixedNumber(
          incorporatedShippingCost - shippingCostProportionedTotal,
          2
        )

        if (lastAdjustedPricing)
          lastAdjustedPricing.total = this.$utils.toFixedNumber(lastAdjustedPricing?.total + roundingAdjustment, 2)

        if (this.fronts?.total !== 0) {
          this.fronts.total = this.$utils.toFixedNumber(this.fronts?.total + incorporatedShippingCost, 2)
        }

        // Early Items — not available for EU (orders must ship all-at-once due to customs/insurance pricing)
        if (this.quote.region !== 'eu') {
          if (this.version.spacerPanels && this.version.spacerPanels.length > 0 && this.version.earlySpacers) {
            deliveryCharge = this.$utils.toFixedNumber(deliveryCharge + productData.additionalCharges?.earlySpacers, 2)
          }
          if (this.version.earlyWorktops) {
            deliveryCharge = this.$utils.toFixedNumber(deliveryCharge + shippingZone?.data().cost, 2)
          }
        }
        this.shipping = this.$utils.toFixedNumber(deliveryCharge, 2)
      }
    },
    updateMaterialUsage() {
      this.materialUsage = {}

      for (let i = 0; i < this.version.materials.length; i++) {
        let material = this.version.materials[i]
        let m = (this.materialUsage[material.id] = {
          areaWithWastage: 0
        })

        // Fronts
        if (this.fronts[material.id] && this.fronts[material.id]?.areaWithWastage) {
          m.areaWithWastage += this.fronts[material.id].areaWithWastage
        }
        // Urtils
        if (this.urtils[material.id] && this.urtils[material.id]?.areaWithWastage) {
          m.areaWithWastage += this.urtils[material.id].areaWithWastage
        }
        // Pantries
        if (this.pantries[material.id] && this.pantries[material.id]?.areaWithWastage) {
          m.areaWithWastage += this.pantries[material.id].areaWithWastage
        }
        // Spice Racks (laminate-diff and laminate-main - tracked in ukPricing.spiceRacks)
        if (this.spiceRacks[material.id] && this.spiceRacks[material.id]?.areaWithWastage) {
          m.areaWithWastage += this.spiceRacks[material.id].areaWithWastage
        }

        // if there is no areaWithWastage, remove material from usage
        if (m.areaWithWastage === 0) {
          delete this.materialUsage[material.id]
          continue // skip to next material
        }
      }
    },
    async updateDeliveryLocations() {
      this.deliveryLocations = []
      const shippingZonesSnap = await getDocs(
        query(collection(db, 'shippingZones'), where('regions', 'array-contains', this.quote.region))
      )
      this.deliveryLocations = shippingZonesSnap.docs
      return true
    },
    convertedValue(value) {
      return this.version.exchangeRates && this.version.currency !== 'GBP'
        ? this.$utils.toFixedNumber(value * this.version.exchangeRates?.rates[this.version.currency], 2)
        : value
    },
    addMaterial() {
      this.version.materials.push({ id: uid(10) })
    },
    removeMaterial(key) {
      const material = this.version.materials[key]

      // Straight up delete if empty material (only has id)
      if (Object.keys(material).length === 1) {
        this.version.materials.splice(key, 1)
        return
      }

      // Ensure pricing data is loaded before checking usage
      if (!this.fronts || !this.urtils || !this.pantries || !this.spiceRacks) {
        this.$store.commit('openSnackbar', {
          snackbarText: 'Please wait for pricing to load before removing materials.'
        })
        return
      }

      const { inUse, usedIn } = this.isMaterialInUse(material.id)

      if (!inUse) {
        // Show delete confirmation dialog
        this.materialDeleteParams = { key, label: material.label || 'Unnamed' }
        this.materialDeleteVisible = true
        return
      }

      // Material is in use - check if reassignment is possible
      const compatibleMaterials = this.version.materials.filter((m) => {
        if (m.id === material.id) return false
        if (m.thickness !== material.thickness) return false

        // Veneer can replace veneer
        if (material.hasGrain && m.hasGrain) return true
        // Same sides can replace each other
        if (material.sides === m.sides) return true
        // Veneer can work as single or double sided in some cases
        if (material.hasGrain || m.hasGrain) return true
        // Allow single↔double for materials used only in Fronts (no sides restrictions)
        if (this.isMaterialUsedOnlyInFronts(material.id)) return true

        return false
      })

      if (compatibleMaterials.length === 0) {
        const sidesText = material.hasGrain ? 'veneer' : material.sides === 1 ? 'single-sided' : 'double-sided'
        this.$store.commit('openSnackbar', {
          snackbarText: `Cannot delete: used in ${usedIn.join(', ')}. No compatible ${
            material.thickness
          }mm ${sidesText} material to reassign to.`
        })
        return
      }

      // Show reassign dialog
      this.materialReassignParams = {
        removeId: material.id,
        removeKey: key,
        thickness: material.thickness,
        sides: material.sides,
        hasGrain: material.hasGrain,
        usedIn: usedIn,
        frontsOnly: this.isMaterialUsedOnlyInFronts(material.id)
      }
      this.materialReassignVisible = true
    },
    confirmDeleteMaterial() {
      this.version.materials.splice(this.materialDeleteParams.key, 1)
      this.materialDeleteVisible = false
      this.materialDeleteParams = {}
    },
    getMaterial(code) {
      const mat = this.version.materials.find((material) => {
        return code === material.id
      })
      return mat
    },
    reassignMaterial() {
      /**
       * Reassign materials by item;
       */
      let invalidReassign = false
      const reassignMaterial = this.version.materials.find(
        (material) => material.id == this.materialReassignParams.replace
      )
      // Urtil
      let urtilsItems = this.version.urtils
      for (let i = 0; i < urtilsItems.length; i++) {
        // check carcass
        if (urtilsItems[i].carcass == this.materialReassignParams.removeId) {
          urtilsItems[i].carcass = this.materialReassignParams.replace
        }

        // check back
        if (urtilsItems[i].back == this.materialReassignParams.removeId) {
          // check if reassign back is double-sided, invalidate
          if (reassignMaterial?.sides == 2) {
            invalidReassign = true
          } else if (reassignMaterial?.hasGrain || reassignMaterial?.sides == 1) {
            urtilsItems[i].back = this.materialReassignParams.replace
          }
        }

        // check door
        let urtilType = urtilsItems[i].type
        if (urtilType != 'o') {
          // check if door is double sided or veneer
          if (reassignMaterial?.hasGrain || reassignMaterial?.sides == 2) {
            if (urtilType == 's') {
              if (urtilsItems[i].material1 == this.materialReassignParams.removeId) {
                urtilsItems[i].material1 = this.materialReassignParams.replace
              }
            } else if (urtilType == 't') {
              if (urtilsItems[i].material1 == this.materialReassignParams.removeId) {
                urtilsItems[i].material1 = this.materialReassignParams.replace
              }
              if (urtilsItems[i].material2 == this.materialReassignParams.removeId) {
                urtilsItems[i].material2 = this.materialReassignParams.replace
              }
            }
          } else {
            // cant be single sided
            invalidReassign = true
          }
        }
      }

      // Pantry
      let pantriesItems = this.version.pantries
      for (let i = 0; i < pantriesItems.length; i++) {
        // check base
        const pantryType = pantriesItems[i].pantryType
        if (pantryType == 'l' && pantriesItems[i].base == this.materialReassignParams.removeId) {
          if (reassignMaterial.hasGrain || reassignMaterial.id == pantriesItems[i].carcass) {
            // cant have veneer or carcass matching for laminate pantry base
            invalidReassign = true
          } else {
            pantriesItems[i].base = this.materialReassignParams.replace
          }
        }

        // check carcass
        if (pantriesItems[i].carcass == this.materialReassignParams.removeId) {
          if (pantryType == 'l' && pantriesItems[i].base == reassignMaterial.id) {
            // can't replace carcass with matching laminate base
            invalidReassign = true
          } else if (pantryType != 'l' || (pantryType == 'l' && pantriesItems[i].base != reassignMaterial.id)) {
            pantriesItems[i].carcass = this.materialReassignParams.replace
          }
        }

        // check back
        if (pantriesItems[i].back == this.materialReassignParams.removeId) {
          if (reassignMaterial?.sides == 2) {
            invalidReassign = true
          } else if (reassignMaterial?.hasGrain || reassignMaterial?.sides == 1) {
            pantriesItems[i].back = this.materialReassignParams.replace
          }
        }
      }

      // Spice Racks
      let spiceRackItems = this.version.spiceRacks || []
      for (let i = 0; i < spiceRackItems.length; i++) {
        if (spiceRackItems[i].frame == this.materialReassignParams.removeId) {
          spiceRackItems[i].frame = this.materialReassignParams.replace
        }
      }

      // Fronts
      let frontsItems = this.version.fronts.concat(this.version.coverPanels)
      let handleItems = this.version.handles
      let reassignHandles = [] // for storing new reassign handles

      handleItems.forEach((handle) => {
        if (handle.insert == this.materialReassignParams.removeId) {
          // Handle inserts must be veneer or single-sided (no double-sided)
          if (reassignMaterial?.sides == 2 && !reassignMaterial?.hasGrain) {
            invalidReassign = true
            return
          }

          // create new handle, copy data from old handle, but replace insert
          let newHandle = {
            id: uid(10),
            removeId: handle.id,
            insert: this.materialReassignParams.replace,
            orientation: handle.orientation,
            value: handle.value
          }
          // add handle labal
          this.constructHandleLabel(newHandle)

          // store new handles
          reassignHandles.push(newHandle)
        }
      })

      // replace each handle from stored reassignHandles
      reassignHandles.forEach((newHandle) => {
        frontsItems.forEach((front, index) => {
          if (front?.handle && front?.handle == newHandle.removeId) {
            // then replace fronts handles with new handle
            this.version.fronts[index].handle = newHandle.id
          }
        })
      })

      // replace old handle with new
      reassignHandles.forEach((newHandle) => {
        handleItems.forEach((handle, index) => {
          if (handle.id == newHandle.removeId) {
            delete newHandle.removeId
            this.version.handles.splice(index, 1, newHandle)
          }
        })
      })

      // check main material
      for (let i = 0; i < frontsItems.length; i++) {
        if (frontsItems[i].material == this.materialReassignParams.removeId) {
          frontsItems[i].material = this.materialReassignParams.replace
        }
      }

      // after all reassign material item check, delete removing material and reset reassign params
      if (!invalidReassign) {
        this.version.materials.splice(this.materialReassignParams.removeKey, 1)
      } else {
        if (reassignMaterial.sides == 2 && reassignMaterial.thickness == 6) {
          // urtil/pantry back needs to be single-sided
          this.$store.commit('openSnackbar', {
            snackbarText: 'Material cannot be reassigned; urtil/pantry back needs to be single sided!'
          })
        } else if (reassignMaterial.sides == 1 && reassignMaterial.thickness == 6) {
          // urtil door needs to be double-sided
          this.$store.commit('openSnackbar', {
            snackbarText: 'Material cannot be reassigned; urtil door needs to be double sided!'
          })
        } else if (reassignMaterial.sides == 2 && reassignMaterial.thickness == 4) {
          // handle insert cannot be double-sided
          this.$store.commit('openSnackbar', {
            snackbarText: 'Material cannot be reassigned; handle insert cannot be double sided!'
          })
        } else {
          // laminate base matching pantry carcass (or vice versa)
          this.$store.commit('openSnackbar', {
            snackbarText: 'Material cannot be reassigned; pantry laminate base cannot match carcass!'
          })
        }
      }
      this.materialReassignParams = {}
      this.materialReassignVisible = false
    },
    checkReassignMaterial() {
      this.reassignMaterialWarning = ''
      const replaceMaterial = this.version.materials.find(
        (material) => material.id == this.materialReassignParams.replace
      )

      // Check if this material is used in components with sides restrictions
      const usedInUrtilBack = this.version.urtils?.some((u) => u.back == this.materialReassignParams.removeId)
      const usedInUrtilDoor = this.version.urtils?.some(
        (u) =>
          u.material1 == this.materialReassignParams.removeId || u.material2 == this.materialReassignParams.removeId
      )
      const usedInPantryBack = this.version.pantries?.some((p) => p.back == this.materialReassignParams.removeId)
      const usedInHandleInsert = this.version.handles?.some((h) => h.insert == this.materialReassignParams.removeId)

      // Only warn about sides mismatch if used in restricted components
      if (usedInUrtilBack || usedInPantryBack) {
        // Backs must be single-sided or veneer
        if (replaceMaterial?.sides == 2) {
          this.reassignMaterialWarning = 'Urtil/pantry backs require single-sided or veneer material!'
        }
      } else if (usedInUrtilDoor) {
        // Doors must be double-sided or veneer
        if (replaceMaterial?.sides == 1 && !replaceMaterial?.hasGrain) {
          this.reassignMaterialWarning = 'Urtil doors require double-sided or veneer material!'
        }
      } else if (usedInHandleInsert) {
        // Handle inserts must be veneer or single-sided (no double-sided)
        if (replaceMaterial?.sides == 2 && !replaceMaterial?.hasGrain) {
          this.reassignMaterialWarning = 'Handle inserts cannot be double-sided!'
        }
      }
      // No warning for fronts - they can freely switch between single and double-sided
    },
    reconcileMaterialFromLive(slot, resolved) {
      if (!resolved) return

      // 1. Intrinsic descriptors always mirror the doc.
      slot.hasGrain = resolved.hasGrain
      slot.requiresColourCode = !resolved.hasGrain && resolved.colours !== undefined

      if (resolved.hasGrain) {
        delete slot.sides
        delete slot.colourCode
        if (slot.continuousGrain === undefined) slot.continuousGrain = true
        if (slot.grainDirection === undefined) slot.grainDirection = 'h'
      } else {
        delete slot.continuousGrain
        delete slot.grainDirection
      }

      const sizes = resolved.sizes || []

      // 3. Thickness must still be offered; clearing it invalidates size/sides.
      if (slot.thickness != null && !sizes.some((s) => s.thickness === slot.thickness)) {
        delete slot.thickness
        delete slot.size
        delete slot.w
        delete slot.h
        delete slot.sides
      }

      // 4. Size must still be offered at the (surviving) thickness.
      if (slot.size != null && slot.thickness != null) {
        const sizeValid = sizes.some((s) => s.thickness === slot.thickness && `${s.w}x${s.h}` === slot.size)
        if (!sizeValid) {
          delete slot.size
          delete slot.w
          delete slot.h
          delete slot.sides
        }
      }

      // 5. Sides (laminate) must still be offered at that thickness + size.
      if (!resolved.hasGrain && slot.sides != null && slot.thickness != null && slot.size != null) {
        const [w, h] = slot.size.split('x').map((n) => parseInt(n))
        const sidesValid = sizes.some(
          (s) => s.thickness === slot.thickness && s.w === w && s.h === h && s.sides === slot.sides
        )
        if (!sidesValid) delete slot.sides
      }

      // 6. Colour must still exist on the doc.
      if (slot.colourCode != null && !(resolved.colours || []).some((c) => c.value === slot.colourCode)) {
        delete slot.colourCode
      }

      // 7. Refresh derived UI fields (display colour + label) from the doc.
      const colour = slot.colourCode ? (resolved.colours || []).find((c) => c.value === slot.colourCode) : null
      slot.displayColour = colour ? colour.hex : resolved.texture?.hex
      slot.label = colour ? `${resolved.text} ${colour.text}` : resolved.text
    },
    onMaterialChange(key, value) {
      const newMaterial = this.masterMaterials.find((m) => m.value === value)

      delete this.version.materials[key].materialId

      this.version.materials[key].hasGrain = newMaterial.hasGrain
      this.version.materials[key].requiresColourCode = !newMaterial.hasGrain && newMaterial.colours !== undefined

      // set default ui colours
      this.version.materials[key].displayColour = newMaterial.texture.hex

      if (newMaterial.hasGrain) {
        this.version.materials[key].continuousGrain = true
        this.version.materials[key].grainDirection = 'h'
        delete this.version.materials[key].colourCode
        delete this.version.materials[key].sides
      } else {
        delete this.version.materials[key].continuousGrain
        delete this.version.materials[key].grainDirection
      }

      this.updateMaterialLabel(key)
      this.updateHandleLabels()
    },
    onMaterialSizeChange(key, size) {
      const dimensions = size.split('x')
      this.version.materials[key].w = parseInt(dimensions[0])
      this.version.materials[key].h = parseInt(dimensions[1])
      // this.updateMaterialLabel(key)
    },
    onColourChange(key) {
      const slot = this.version.materials[key]
      const material = MaterialResolver.resolveMaterial(slot)
      const colour = material.colours.find((m) => m.value === slot.colourCode)
      slot.displayColour = colour.hex

      this.updateMaterialLabel(key)
    },
    updateMaterialLabel(key) {
      const material = this.version.materials[key]
      const masterMaterial = MaterialResolver.resolveMaterial(material)
      if (!masterMaterial) return
      const colour = material.colourCode
        ? masterMaterial.colours.find((colour) => colour.value === material.colourCode)
        : null

      let label = masterMaterial.text
      if (colour) label += ' ' + colour.text

      // Removing as should grab size from data if needed
      // if (material.size) label += ' ' + material.size

      this.version.materials[key].label = label
    },
    addAdditionalItem(excludeFromPacked) {
      if (this.version.additionalItems == undefined) {
        this.version.additionalItems = []
      }
      this.version.additionalItems.push({
        description: null,
        value: null,
        discount: 0,
        excludeFromPacked: excludeFromPacked
      })
    },
    removeAdditionalItems(item) {
      const itemIndex = this.version.additionalItems.findIndex((o) => {
        return o === item
      })
      this.version.additionalItems.splice(itemIndex, 1)
    },
    addHandle() {
      this.version.handles.push({ id: uid(10) })
    },
    updateHandleLabels() {
      for (const handle of this.version.handles) {
        this.constructHandleLabel(handle)
      }
    },
    constructHandleLabel(handle) {
      if (!this.$store.state.handles) return

      const h = this.$store.state.handles.find((h) => {
        return h.value === handle.value
      })

      if (h) {
        var n

        if (!h.orientations) {
          delete handle.orientation
          n = h.text
        } else {
          n = productData.handleOrientations[handle.orientation] + ' - ' + h.text
          if (!handle.orientation) handle.orientation = null
        }

        if (h && h.hasInsert) {
          if (!handle.insert) {
            handle.insert = null
          } else {
            for (var i = 0; i < this.version.materials.length; i++) {
              if (handle.insert == this.version.materials[i].id) {
                n += ' / ' + this.version.materials[i].label
                break
              }
            }
          }
        } else {
          delete handle.insert
        }
        handle.label = n
      }
    },
    requiresInsert(handle) {
      if (!this.$store.state.handles) return false

      const h = this.$store.state.handles.find((h) => {
        return h.value === handle.value
      })
      return h && h.hasInsert ? h.hasInsert : false
    },
    getOrientations(handle) {
      if (!this.$store.state.handles) return null

      const h = this.$store.state.handles.find((h) => {
        return h.value === handle.value
      })

      if (h && h.orientations) {
        let a = []
        for (let o of h.orientations) {
          a.push({
            value: o,
            text: this.productData.handleOrientations[o]
          })
        }
        return a
      } else {
        return null
      }
    },
    removeHandle(key) {
      const handle = this.version.handles[key]
      const inUse = this.isHandleInUse(handle.id)

      if (!inUse) {
        // Show delete confirmation dialog
        this.handleDeleteParams = { key, label: handle.label || 'Unnamed' }
        this.handleDeleteVisible = true
        return
      }

      // Handle is in use - check if reassignment is possible
      if (this.version.handles.length === 1) {
        this.$store.commit('openSnackbar', {
          snackbarText: 'Cannot delete: handle is in use and no other handles to reassign to.'
        })
        return
      }

      // If only two handles, auto-reassign to the other one
      if (this.version.handles.length === 2) {
        this.handleReassignParams = {
          removeId: handle.id,
          removeKey: key,
          replace: key === 0 ? this.version.handles[1].id : this.version.handles[0].id
        }
        this.reassignHandle()
        return
      }

      // Show reassign dialog
      this.handleReassignParams = { removeId: handle.id, removeKey: key }
      this.handleReassignVisible = true
    },
    confirmDeleteHandle() {
      this.version.handles.splice(this.handleDeleteParams.key, 1)
      this.handleDeleteVisible = false
      this.handleDeleteParams = {}
    },
    duplicateHandle(id) {
      let clone = { ...this.version.handles[id] }
      clone.id = uid(10)
      this.version.handles.push(clone)
    },
    reassignHandle() {
      for (var i = 0; i < this.version.fronts.length; i++) {
        if (this.version.fronts[i].handle == this.handleReassignParams.removeId) {
          this.version.fronts[i].handle = this.handleReassignParams.replace
        }
      }

      this.version.handles.splice(this.handleReassignParams.removeKey, 1)
      this.handleReassignParams = {}
      this.handleReassignVisible = false
    },
    //
    supersedingMaterialName(slot) {
      const live = MaterialResolver.getSupersedingLiveMaterial(slot)
      return live ? live.text : null
    },
    materialItems(slot) {
      const resolved = MaterialResolver.resolveMaterial(slot)
      if (resolved && !this.masterMaterials.some((m) => m.value === resolved.value)) {
        return [...this.masterMaterials, resolved]
      }
      return this.masterMaterials
    },
    discontinuedMaterialName(slot) {
      if (!MaterialResolver.isMaterialDiscontinued(slot)) return null
      const resolved = MaterialResolver.resolveMaterial(slot)
      return (resolved && resolved.text) || slot.label || slot.value || 'this material'
    },
    getMaterialThicknesses(slot) {
      const material = MaterialResolver.resolveMaterial(slot)
      // [] rather than null, for the same reason as getColourCodes below.
      if (!material) return []

      let thicknesses = []
      for (let size of material.sizes) {
        if (!thicknesses.some((o) => o.value === size.thickness)) {
          thicknesses.push({
            text: size.thickness + 'mm',
            value: size.thickness
          })
        }
      }
      return thicknesses
    },
    getSheetSizes(slot, thickness) {
      const material = MaterialResolver.resolveMaterial(slot)
      if (!material) return []

      let sizes = []
      // TODO add label to DB and include in text eg half sheet 4mm handle inserts
      //
      // `material.sizes` holds one row per (w, h, thickness, sides) combination, but this list is
      // keyed on dimensions alone — sides is the NEXT dropdown's job (getSheetSides below, which
      // re-reads the rows for whichever dimensions get picked). So a material stocked both single-
      // and double-sided at one dimension contributed two rows that differ only in `sides`, and
      // they rendered as two identical options. They also both read as SELECTED when either was
      // picked: VList keys each option on `item.value` and derives selection by matching it, so
      // two options sharing a value light up together.
      //
      // Same de-dup as getMaterialThicknesses above, which collapses the same rows by thickness.
      for (let size of material.sizes) {
        if (size.thickness == thickness) {
          const value = size.w + 'x' + size.h
          if (sizes.some((o) => o.value === value)) continue
          sizes.push({
            text: size.w + ' X ' + size.h,
            value
          })
        }
      }
      return sizes
    },
    getSheetSides(slot, thickness, size) {
      if (!slot || !thickness || !size) return []

      const dimensions = size.split('x')
      const material = MaterialResolver.resolveMaterial(slot)
      if (!material) return []

      let sides = []
      // TODO add label to DB and include in text eg half sheet 4mm handle inserts
      for (let side of material.sizes) {
        if (side.thickness == thickness && side.w === parseInt(dimensions[0]) && side.h === parseInt(dimensions[1])) {
          sides.push({
            text: side?.sides === 1 ? 'Single' : 'Double',
            value: side.sides
          })
        }
      }
      return sides
    },
    getColourCodes(slot) {
      const material = MaterialResolver.resolveMaterial(slot)
      if (!material) return []
      return material.colours || []
    },
    updateCog() {
      for (let material of this.version.materials) {
        let m = this.fronts[material.id]
        if (m?.pricing) {
          // update COGS for each material
          m.pricing.cogs = this.calcCog(material)
        }
      }
    },
    calcCog(material) {
      if (this.quote.region !== 'uk') {
        // COGS only applies to UK region
        return 0
      }
      // check if material exists in fronts
      let m = this.fronts[material.id]

      let finalTotal = m.total
      if (this.version.discount && this.version.discount > 0) {
        // apply discount to COGS
        finalTotal = this.$utils.toFixedNumber(finalTotal * (1 - this.version.discount / 100), 2)
      }

      // console.log(
      //   'calcCog for material:',
      //   material,
      //   'finalTotal:',
      //   finalTotal,
      //   'pricing:',
      //   m.pricing,
      //   'incorporatedShippingCost:',
      //   m.incorporatedShippingCost * 2
      // )
      let cogs = ((m.pricing.external + m.incorporatedShippingCost * 2) / finalTotal) * 100 // we do x2 incorporatedShippingCost since total has incorporatedShippingCost as well

      return this.$utils.toFixedNumber(cogs, 0)
    },
    legacyDefaultGrain() {
      if (this.version.grainDirection) return
      // console.log('legacyDefaultGrain set to:', this.findGrainDirection())
      this.version.grainDirection = this.findGrainDirection()
    },
    getItemListOpposingGrain() {
      let itemListOpposingGrain = []
      for (let cp of this.version.coverPanels) {
        if (cp.opposingGrain) {
          itemListOpposingGrain.push(cp)
        }
      }
      for (let urtil of this.version.urtils) {
        if (urtil.opposingGrain) {
          itemListOpposingGrain.push(urtil)
        }
      }
      this.itemListOpposingGrain = itemListOpposingGrain
    },
    resetItemOpposingGrain() {
      for (let cp of this.version.coverPanels) {
        if (cp.opposingGrain) {
          cp.opposingGrain = false
        }
      }
      for (let urtil of this.version.urtils) {
        if (urtil.opposingGrain) {
          urtil.opposingGrain = false
        }
      }
      this.itemListOpposingGrain = []
    },
    legacyUPlight() {
      // make all legacy UP Lights to be centrally positioned
      for (let cp of this.version.coverPanels) {
        if (cp.lighting && cp.lightingRef == '') {
          cp.lightingRef = 'c'

          this.$store.commit('openSnackbar', {
            snackbarText: 'Legacy Underside Panel Lighting; position kept central.'
          })
        }
      }
    },
    checkForPreAssembledUrtil() {
      let foundPreAssembledUrtil = this.version.urtils.filter((urtil) => urtil.preAssembled)
      // Make Prod Notes
      if (!this.version.notes) this.version.notes = ''

      if (foundPreAssembledUrtil.length > 0 && !this.version.notes.includes('//Pre-assembled Urtil included.')) {
        this.version.notes += '//Pre-assembled Urtil included.'
      } else if (foundPreAssembledUrtil.length == 0 && this.version.notes.includes('//Pre-assembled Urtil included.')) {
        let splitNotes = this.version.notes.split('//Pre-assembled Urtil included.')
        let cleanNotesArray = splitNotes.filter((sentence) => sentence != '//Pre-assembled Urtil included.')
        cleanNotesArray.length > 1
          ? (this.version.notes = cleanNotesArray.join(''))
          : cleanNotesArray.length == 1
          ? (this.version.notes = cleanNotesArray[0])
          : (this.version.notes = '')
      }

      // PreAssembly Cost
      let foundPreAssemblyCost
      if (this.version.additionalItems) {
        foundPreAssemblyCost = this.version.additionalItems.find(
          (item) => item.description == 'Urtil Pre-Assembly Cost'
        )
      } else {
        this.version.additionalItems = []
      }

      if (foundPreAssembledUrtil.length > 0 && !foundPreAssemblyCost) {
        this.version.additionalItems.push({
          description: 'Urtil Pre-Assembly Cost',
          discount: 0,
          excludeFromPacked: true,
          value: this.$store.state.pricing.costs.preAssemblyCostPerUrtil.value * foundPreAssembledUrtil.length
        })
      } else if (foundPreAssembledUrtil.length > 0 && foundPreAssemblyCost) {
        if (
          foundPreAssemblyCost.value !==
          this.$store.state.pricing.costs.preAssemblyCostPerUrtil.value * foundPreAssembledUrtil.length
        ) {
          foundPreAssemblyCost.value =
            this.$store.state.pricing.costs.preAssemblyCostPerUrtil.value * foundPreAssembledUrtil.length
        }
      } else if (foundPreAssembledUrtil.length == 0 && foundPreAssemblyCost) {
        this.version.additionalItems = this.version.additionalItems.filter(
          (item) => item.description != 'Urtil Pre-Assembly Cost'
        )
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.v-card-title.text-subtitle-1 {
  font-weight: 400;
}

.panel-row {
  align-items: center;

  > .v-col.d-flex {
    align-items: center;
  }

  &:first-child {
    margin-top: 0;
  }

  :deep(.v-field) {
    --v-field-border-width: 2px;
  }
}

.panel-row--unlabelled {
  .v-input {
    --v-input-padding-top: 0px; // `0px`, not `0` — it is consumed inside a `calc()`
  }
  .v-input,
  :deep(.v-field) {
    --v-input-control-height: 40px;
  }
  &.panel-row :deep(.v-field) {
    --v-field-border-width: 3px;
  }
  :deep(.v-field.v-field--variant-underlined .v-field__append-inner),
  :deep(.v-field.v-field--variant-underlined .v-field__clearable) {
    align-items: center;
    padding-top: 0;
    padding-bottom: 0;
  }
}

// The Default Grain card sits beside the Grain Direction select and holds only its subtitle until
// some item picks up an opposing grain, so at rest it came out barely half the height of the field
// next to it — and each chip appearing or clearing resized it again.
//
// 48px is not a guess: it is `--v-input-control-height` for the select beside it, which Vuetify
// sets from density + variant (`.v-input--density-default .v-field--variant-underlined`,
// VField.css:99). That variant is this app's global VSelect default (plugins/vuetify.js), and the
// column's `align-start` already lines the two tops up, so matching it makes the pair read as one
// row rather than a tall box next to a short one.
//
// `min-height`, not `height`: the resting size is fixed, but the chip list still pushes the card
// taller once it outgrows that — which is the whole point of the card. `height` would instead clip
// the chips (VCard is `overflow: hidden`) and the overflowing ones would simply vanish.
.default-grain {
  min-height: 48px;

  .v-card-subtitle {
    line-height: 48px;
  }
}

.material-options {
  :deep(.v-checkbox.v-input) {
    flex-shrink: 0;
  }
  :deep(.v-selection-control .v-label) {
    white-space: nowrap;
  }
}

#early-items {
  .v-switch {
    margin-top: 0;
  }
}
.colour {
  min-width: 25px;
  width: 25px;
  height: 25px;
  border: solid 1px #999;
  border-radius: 3px;
  margin-right: 12px;
}
.v-card-title {
  padding: 8px 16px;
}
.colour-box {
  min-width: 25px;
  height: 25px;
  border: solid 1px #999;
  border-radius: 3px;
  margin-right: 10px;
}
.compact {
  .v-input {
    margin-top: 0px;
    padding-top: 0px;
    &.label-active {
      margin-top: 12px;
    }
  }
}
</style>
